import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ClinicDialog } from './ClinicDialog';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import promptService, { Prompt, PromptProvider, PromptThinkingLevel, MODEL_OPTIONS } from '../../services/promptService';
import { useAuth } from '../../context/AuthContext';
import { getApiError } from '../../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: number;
}

export const RunQuestionnaireSummaryDialog: React.FC<Props> = ({ open, onClose, patientId }) => {
  const { user } = useAuth();

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<number | ''>('');
  const [action, setAction] = useState<'update_summary' | 'send_summary'>('send_summary');
  const [provider, setProvider] = useState<PromptProvider | ''>('');
  const [model, setModel] = useState<string>('');
  const [thinkingLevel, setThinkingLevel] = useState<PromptThinkingLevel | ''>('');
  const [loading, setLoading] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQueued(false);
    setError(null);
    setSelectedPromptId('');
    setProvider('');
    setModel('');
    setThinkingLevel('');

    promptService
      .getPrompts('questionnaire_summary', true)
      .then((all) => {
        setPrompts(all);
        if (all.length > 0) {
          const first = all[0];
          setSelectedPromptId(first.id);
          setProvider((first.model_provider as PromptProvider) || '');
          setModel(first.model || '');
          setThinkingLevel((first.thinking_level as PromptThinkingLevel) || '');
        }
      })
      .catch(() => setPrompts([]));
  }, [open, user?.tenant_id]);

  const handlePromptChange = (promptId: number | '') => {
    setSelectedPromptId(promptId);
    const p = prompts.find((x) => x.id === promptId);
    if (p) {
      setProvider((p.model_provider as PromptProvider) || '');
      setModel(p.model || '');
      setThinkingLevel((p.thinking_level as PromptThinkingLevel) || '');
    }
  };

  const handleProviderChange = (val: PromptProvider | '') => {
    setProvider(val);
    setModel('');
  };

  const handleRun = async () => {
    if (!selectedPromptId) {
      setError('Please select a prompt.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await promptService.runQuestionnaireSummary(
        patientId,
        selectedPromptId as number,
        action,
        {
          model_provider: provider || null,
          model: model || null,
          thinking_level: thinkingLevel || null,
        },
      );
      setQueued(true);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to queue job'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQueued(false);
    setError(null);
    onClose();
  };

  return (
    <ClinicDialog
      open={open}
      onClose={handleClose}
      title="Run Questionnaire Summary"
      maxWidth="sm"
      actions={
        <>
          {!queued && (
            <Button
              onClick={handleRun}
              variant="contained"
              disabled={loading || !selectedPromptId}
              sx={CLINIC_DIALOG_SX.primaryButton}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              {loading ? 'Queuing...' : 'Run'}
            </Button>
          )}
          <Button onClick={handleClose} sx={CLINIC_DIALOG_SX.cancelButton}>
            {queued ? 'Close' : 'Cancel'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {queued ? (
          <Alert severity="success">
            {action === 'update_summary'
              ? 'Job queued — the summary will be saved to the patient record once complete.'
              : 'Job queued — the summary will be sent to your email once complete.'}
          </Alert>
        ) : (
          <>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Prompt</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedPromptId}
                  onChange={(e) => handlePromptChange(e.target.value as number | '')}
                  displayEmpty
                  style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                >
                  <MenuItem value="" disabled><em>Select prompt...</em></MenuItem>
                  {prompts.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Action</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={action}
                  onChange={(e) => setAction(e.target.value as 'update_summary' | 'send_summary')}
                  style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                >
                  <MenuItem value="update_summary">Update Patient Summary</MenuItem>
                  <MenuItem value="send_summary">Send Summary by Email</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Provider</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={provider}
                    onChange={(e) => handleProviderChange(e.target.value as PromptProvider | '')}
                    displayEmpty
                    style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                  >
                    <MenuItem value=""><em>Default</em></MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Gemini</MenuItem>
                    <MenuItem value="claude">Claude</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 2, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Model</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    displayEmpty
                    style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                  >
                    <MenuItem value=""><em>Default</em></MenuItem>
                    {(MODEL_OPTIONS[provider] || []).map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Thinking</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={thinkingLevel}
                    onChange={(e) => setThinkingLevel(e.target.value as PromptThinkingLevel | '')}
                    displayEmpty
                    style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                  >
                    <MenuItem value=""><em>Default</em></MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </ClinicDialog>
  );
};
